import { gql } from 'apollo-angular';

/**
 * User schema
 */
export class UserSchema {
	/**
	 * All properties
	 */
	static readonly topLevelProperties = `
		uid
		externalUid
		username
		userState
		title
		gender
		firstName
		lastName
		phoneNumber
		emailAddress
		skypeName
		listrange
		acceptedTermsAndConditions
		timezone
		language {
			code
		}
		roles {
			uid
			name
			workflows {
				token
			}
		}
		company {
			uid
			type
			name
			eMailAddress
			phoneNumber
			market {
				countries {
					code
					name
				}
			}
			shippingAddress {
				address1
				address2
				city
				zipCode
				region {
					name
					regionCode
					countryCode
				}
				latitude
				longitude
			}
			billingAddress {
				address1
				address2
				city
				zipCode
				country {
					code
					name
					shutdownDatePurchaser
					shutdownDateContractor
					shutdownPhase
				}
				region {
					name
					regionCode
					countryCode
				}
				latitude
				longitude
			},
			extendedShutdownDate
		}
		signatureData {
			signatureFile {
				filePath
				storageFileNumber
				uid
			}
			signatureText
			useSignature
		}
		`;

	static readonly getUserListProperties = `
		uid
		externalUid
		username
		userState
		title
		gender
		firstName
		lastName
		phoneNumber
		emailAddress
		skypeName
		language {
			code
			displayName
			name
		}
		listrange
		timezone
		roles {
			uid
			name
			workflows {
				token
			}
		}
		signatureData {
			signatureFile {
				filePath
				storageFileNumber
				uid
			}
			signatureText
			useSignature
		}
	`;

	static readonly getUser = gql`
		query User ($uid: String!) {
			user(uid: $uid) {
				${UserSchema.getUserListProperties}
			}
		}
	`;

	static readonly getUserWithCredit = gql`
		query UserAndLoginCredentials($uid: String!) {
			user(uid: $uid) {
				${UserSchema.topLevelProperties}
			}
		}
	`;

	/**
	 * Get users
	 * Die Eigenschaften der 'company'-Eigenschaft sind nur beispielhaft implementiert. Hier
	 * muss noch geprüft werden, welche Felder benötigt werden.
	 */
	static readonly getUserList = gql`
		query GetUserList(
		$filters: [WhereFilter],
		$pageIndex: Int!,
		$resultsPerPage: Int!,
		$orderBy: String,
		$isAscending: String
		  ) {
			userlist(
			filters: $filters,
			pageIndex: $pageIndex,
			resultsPerPage: $resultsPerPage,
			orderBy: $orderBy,
			isAscending: $isAscending
			) {
				totalCount
				list {
					${UserSchema.getUserListProperties}
				}
			}
		}
	`;

	/**
	 * Create User
	 */
	static readonly createUser = gql`
		mutation CreateUser($user: UserInput!, $avatarGuid: ID) {
			createUser(user: $user, avatarGuid: $avatarGuid) {
				${UserSchema.getUserListProperties}
			}
		}
	`;

	/**
	 * Update User
	 */
	static readonly updateUser = gql`
		mutation UpdateUser($uid: ID!, $user: UpdateUserInput, $avatarGuid: ID, $deleteAvatar: Boolean, $deleteSignatureFile: Boolean) {
			updateUser(uid: $uid, user: $user, avatarGuid: $avatarGuid, deleteAvatar: $deleteAvatar, deleteSignatureFile: $deleteSignatureFile) {
				${UserSchema.topLevelProperties}
			}
		}
	`;

	/**
	 * Update User
	 */
	static readonly updateUserForEdit = gql`
		mutation UpdateUser($uid: ID!, $user: UpdateUserInput, $avatarGuid: ID, $deleteAvatar: Boolean) {
			updateUser(uid: $uid, user: $user, avatarGuid: $avatarGuid, deleteAvatar: $deleteAvatar) {
				title
				gender
				firstName
				lastName
				phoneNumber
				emailAddress
				skypeName
				language {
					code
				}
				timezone
			}
		}
	`;

	/**
	 * Change password
	 */
	static readonly changeUserPassword = gql`
		mutation ChangeUserPassword($oldPassword: String!, $newPassword: String!) {
			changeUserPassword(oldPassword: $oldPassword, newPassword: $newPassword)
		}
	`;

	/**
	 * Reset password
	 */
	static readonly setUserPassword = gql`
		mutation SetUserPassword($hash: String!, $newPassword: String!, $hasAcceptedTermsAndConditions: Boolean) {
			setPassword(hash: $hash, newPassword: $newPassword, hasAcceptedTermsAndConditions: $hasAcceptedTermsAndConditions)
		}
	`;

	/**
	 * Update user state
	 */
	static readonly updateUserState = gql`
		mutation UpdateUserState($uid: ID!, $state: UserState!) {
			updateUserState(uid: $uid, state: $state) {
				${UserSchema.topLevelProperties}
			}
		}
	`;

	/**
	 * Check if username is unique
	 */
	static readonly isUsernameUnique = gql`
		query IsUsernameUnique($username: String!) {
			isUsernameUnique(username: $username)
		}
	`;

	/**
	 * Check if user is last company admin
	 */
	static readonly isLastCompanyAdmin = gql`
		query IsLastCompanyAdmin($userUid: ID!) {
			isLastCompanyAdmin(userUid: $userUid)
		}
	`;

	/**
	 * Create new account
	 */
	static readonly createAccount = gql`
		mutation createAccount($isDentist: Boolean!, $user: UserInput!, $company: CompanyInput!) {
			createAccount(isDentist: $isDentist, user: $user, company: $company) {
				uid
				company {
					approvalState
				}
			}
		}
	`;

	/**
	 * Activate new account
	 */
	static readonly activateAccount = gql`
		mutation activateAccount($hash: String!) {
			activateAccount(hash: $hash) {
				activationWasSuccessful
				companyName
				errorCode
			}
		}
	`;

	/**
	 * Get the user password expiration date by the hash
	 */
	static readonly getUserPasswordExpirationDate = gql`
		query getPasswordExpirationDateByHash($hash: String!) {
			getPasswordExpirationDateByHash(hash: $hash)
		}
	`;

	// =====================================================================================================================================
	// Get full company data via User-API
	// =====================================================================================================================================

	/**
	 * Get full company data via User
	 */
	static getUserCompanySchema(schema: any) {
		return gql`
			query User ($uid: String!) {
				user(uid: $uid) {
					uid
					company {
						${schema}
					}
				}
			}
		`;
	}
}
